import { Skeleton, Flex, Typography, Select, Button, Space } from "antd";
import { Link, useLocation, useParams } from "react-router-dom";
import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope";
import { useHandlePagination } from "~hooks/useHandlePagination";
import SeasonTable from "./SeasonTable";
import Page from "../../../Common/Page";
import SearchFilter from "~components/SearchFilter/SearchFilter.tsx";
import PermissionCheck from "~Common/PermissionCheck/index.tsx";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { ImportModal } from "~components/ImportButton/ImportModal.tsx";
import { ExportModal } from "~components/ExportModal/ExportModal.tsx";
import { useExportSeasons, useImportSeasons } from "~pages/Seasons/hooks/index.ts";
import { defaultSeasonsExportFieldNames, seasonsExportFields } from "~pages/Seasons/constants/exportFields.ts";

const PermissionName = PermissionNames.SEASON;

const CompetitionSeasonList = () => {
  const {formatMessage} = useIntl();
  const { state } = useLocation();
  const [searchText, setSearchText] = useState(state?.seasonName || "");
  const [statusFilter, setStatusFilter] = useState(state?.seasonStatus || "ACTIVE");
  const { competitionId: pageCompetitionId } = useParams();

  const {
    pageSize,
    currentPage,
    handlePaginationChange,
    pageSizeOptions
  } = useHandlePagination(50, 1, [50, 100, 500, 1000]);

  const { data: scopeData, isLoading } = useGetCurrentUserPermissionScope();
  const { scope, resources } = scopeData || {};

  const { mutateAsync: startImporting } = useImportSeasons();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportSeasons();

  const competition = resources?.competitions?.[pageCompetitionId];
  const competitionSeasons = useMemo(() => {
    const seasonResources = resources?.seasons || {};

    for (const { competitions } of (scope?.leagueTree || [])) {
      for (const { competitionId, seasonIds } of competitions) {
        if (competitionId === pageCompetitionId) {
          return seasonIds.map((seasonId) => seasonResources[seasonId]);
        }
      }
    }

    return [];
  }, [scope?.leagueTree, resources?.seasons, pageCompetitionId])

  const filteredSeasons = useMemo(
    () => competitionSeasons.filter(({ status, nameLocal }) => {
      if (statusFilter !== "ALL" && status !== statusFilter) {
        return false;
      }

      if (searchText && !nameLocal?.toLowerCase()?.includes(searchText.toLowerCase())) {
        return false;
      }

      return true;
    }),
    [competitionSeasons, statusFilter, searchText],
  );

  const Title = () => {
    if (isLoading) {
      return <Skeleton.Input active={true} />;
    }

    return `${competition?.nameLocal}`;
  };

  return (
    <Page
      title={<Title/>}
      tabTitle={formatMessage(
        {id: "pageTitles.organization.competition.seasons"},
        {competitionName: competition?.nameLocal},
      )}
    >
      <Flex vertical gap={8}>
        <Flex align="center" justify="space-between">
          <Typography.Title level={4}>
            Seasons
          </Typography.Title>
          <Space>
            <Select style={{ width: "200px" }} value={statusFilter} onSelect={setStatusFilter}>
              <Select.Option value={"ALL"}>ALL</Select.Option>
              <Select.Option value={"COMPLETE"}>COMPLETE</Select.Option>
              <Select.Option value={"ACTIVE"}>ACTIVE</Select.Option>
              <Select.Option value={"DRAFT"}>DRAFT</Select.Option>
              <Select.Option value={"INACTIVE"}>INACTIVE</Select.Option>
              <Select.Option value={"PENDING"}>PENDING</Select.Option>
            </Select>
            <SearchFilter searchValue={searchText} doSearch={setSearchText} />
            <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.CREATE, PermissionOperations.UPDATE ]}>
              <ImportModal
                  title={formatMessage({ id: "import.modal.title.seasons" })}
                  description={formatMessage({ id: "import.modal.text.seasons" })}
                  startImporting={startImporting}
              />
            </PermissionCheck>
            <PermissionCheck permissionKeys={[ PermissionName ]} permissionOptions={[ PermissionOperations.READ ]}>
              <ExportModal
                  handleExport={startExport}
                  defaultFields={defaultSeasonsExportFieldNames}
                  groupedFields={seasonsExportFields}
                  localStorageKey="export.seasons"
                  loading={isExportPending}
              />
            </PermissionCheck>
            <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
              <Link to={`add`}>
                <Button type="primary" icon={<i className="fas fa-plus" style={{ marginRight: "5px" }} />}>
                  Season
                </Button>
              </Link>
            </PermissionCheck>
          </Space>
        </Flex>
        <SeasonTable
          loading={isLoading}
          seasonList={filteredSeasons}
          pageSize={pageSize}
          currentPage={currentPage}
          handlePaginationChange={handlePaginationChange}
          pageSizeOptions={pageSizeOptions}
        />
      </Flex>
    </Page>
  );
};

export default CompetitionSeasonList;
